import type { PublicPlayerAppearance } from './cosmetics.js';

export enum MessageType {
  // Conexión
  Connect = 0x0001,
  Welcome = 0x0002,
  Disconnect = 0x0003,
  Error = 0x0004,

  // Gameplay
  Input = 0x0101,
  Snapshot = 0x0102,
  Upgrade = 0x0103,
  UpgradeConfirm = 0x0104,

  // Eventos
  OrbCollected = 0x0201,
  PlayerEliminated = 0x0202,
  ArenaCollapse = 0x0203,
  PlayerCollision = 0x0204,

  // Match
  MatchStart = 0x0301,
  MatchEnd = 0x0302,
  MatchState = 0x0303,
  MatchResultUpdated = 0x0304,
  WalletUpdated = 0x0305,

  // Sistema
  Ping = 0x0401,
  Pong = 0x0402,
  Latency = 0x0403,

  // Lobby / Queue
  JoinQueue = 0x0501,
  LeaveQueue = 0x0502,
  LobbyState = 0x0503,
  QueueStatus = 0x0504,
  VoluntaryLeave = 0x0506,
}

export interface MessageHeader {
  type: MessageType;
  length: number;
}

export interface ConnectMessage {
  token: string;
  version: string;
  cosmetics: {
    skinId: string;
    trailId: string;
    collisionEffectId: string;
  };
}

export interface WelcomeMessage {
  playerId: string;
  token: string;
  serverTick: number;
  config: {
    tickRate: number;
    arenaRadius: number;
    maxPlayers: number;
  };
  cosmetics?: Array<{ user_id: string; cosmetic_id: string; unlocked_at: string; source: string; source_reference: string | null }>;
  loadout?: {
    userId: string;
    baseColorId: string | null;
    skinId: string | null;
    patternId: string | null;
    flagId: string | null;
    letterId: string | null;
    accessoryId: string | null;
    trailId: string | null;
    nameplateId: string | null;
    badgeId: string | null;
    victoryEffectId: string | null;
  } | null;
  wallet?: { softCurrency: number };
}

export interface InputMessage {
  sequence: number;
  timestamp: number;
  dirX: number;
  dirY: number;
  inputMode: 'wasd' | 'mouse' | 'touch';
}

export interface SnapshotMessage {
  tick: number;
  lastSequence: number;
  players: CompressedPlayer[];
  orbs: CompressedOrb[];
  arena: {
    radius: number;
    segments: { start: number; end: number }[];
  };
}

export interface CompressedPlayer {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  alive: boolean;
  knockbackTicks: number;
  orbs: number;
  upgrades: { p: number; w: number; sp: number; c: number };
}

export type CollisionType = 'frontal' | 'lateral' | 'rear' | 'glancing' | 'balanced';

export interface PlayerCollisionEvent {
  id: string;
  serverTick: number;
  playerAId: string;
  playerBId: string;
  position: { x: number; y: number };
  normal: { x: number; y: number };
  approachSpeedA: number;
  approachSpeedB: number;
  impactA: number;
  impactB: number;
  netImpact: number;
  impulseA: { x: number; y: number };
  impulseB: { x: number; y: number };
  collisionType: CollisionType;
}

export interface MatchStartPlayer {
  playerId: string;
  name: string;
  appearance: PublicPlayerAppearance;
}

export interface MatchStartMessage {
  roomId: string;
  players: MatchStartPlayer[];
}

export interface CompressedOrb {
  id: number;
  x: number;
  y: number;
}

export interface MatchResultUpdatedMessage {
  matchId: string;
  playerId: string;
  placement: number;
  totalPlayers: number;
  eliminations: number;
  orbsCollected: number;
  survivalMs: number;
  won: boolean;
  exitReason: string;
  rewards: {
    completion: number;
    placementTop10: number;
    placementTop5: number;
    placementTop3: number;
    victory: number;
    eliminations: number;
    firstDailyWin: number;
    abandonPenalty: number;
    total: number;
  };
  balanceBefore: number;
  balanceAfter: number;
}

export interface WalletUpdatedMessage {
  softCurrency: number;
}
